import { useState, useEffect } from "react";
import { USER_TYPE_OPTIONS } from "@/lib/userTypes";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Reusable multi-select picker for "who are you on Your Dog Days".
 * Same UI everywhere we ask — sign-up modal, Vet & Health card, Settings card.
 * `onSaved` is called with the new list AFTER the server confirms; consumers
 * can use it to dismiss a modal or refresh related views.
 */
export default function UserTypePicker({
  initial = [],
  onSaved,
  saveLabel = "Save",
  showSecondary = false,
  secondaryLabel = "Skip for now",
  onSecondary,
  data_testid = "user-type-picker",
}) {
  const [selected, setSelected] = useState(new Set(initial || []));
  const [saving, setSaving] = useState(false);
  const { updateProfile } = useAuth();

  // Re-sync when parent passes a different `initial` (e.g. picker reopens with
  // the fresh server state). Used by the Vet & Health card after refetch.
  useEffect(() => { setSelected(new Set(initial || [])); }, [initial]);

  const toggle = (k) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    const list = USER_TYPE_OPTIONS.map((o) => o.key).filter((k) => selected.has(k));
    try {
      await updateProfile({ user_types: list });
      toast.success("Got it — saved to your profile");
      onSaved?.(list);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={data_testid}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {USER_TYPE_OPTIONS.map((o) => {
          const on = selected.has(o.key);
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => toggle(o.key)}
              data-testid={`user-type-option-${o.key}`}
              aria-pressed={on}
              className={`flex items-start gap-3 p-3 rounded-md border text-left transition-colors ${on ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
            >
              <span className={`mt-0.5 h-5 w-5 rounded border flex items-center justify-center shrink-0 ${on ? "bg-primary border-primary text-primary-foreground" : "border-border bg-card"}`}>
                {on && <Check size={12} />}
              </span>
              <span className="min-w-0">
                <span className="font-medium text-sm block">{o.label}</span>
                <span className="text-xs text-muted-foreground block leading-snug">{o.blurb}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-5">
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid={`${data_testid}-save`}
        >
          {saving ? "Saving…" : saveLabel}
        </Button>
        {showSecondary && (
          <Button
            type="button"
            variant="ghost"
            onClick={onSecondary}
            disabled={saving}
            data-testid={`${data_testid}-secondary`}
          >
            {secondaryLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
